import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { setGlobalPageSize } from '../../../core/store/actions/app.actions';
import { Message } from '../../models/message';
import * as actions from '../actions/messages.actions';

export interface MessagesState extends EntityState<Message> {
  baseState: baseState.BaseState;
  selectedMessageId: number | null;
  newMessageDraft: { providerUserId: string; messageTopicId: number } | null;
  isMessageResponsesLoading: boolean;
  /**
   * Failures from the message-thread (responses) read, kept apart from `baseState.error` so a failed
   * thread cannot make the messages list render its error state. See PBI 54129.
   */
  detailError: unknown;
}

export const messagesAdapter = createEntityAdapter<Message>({
  selectId: message => message.messageId,
  sortComparer: (a, b) => b.modifiedOnUtc.localeCompare(a.modifiedOnUtc),
});

export const initialState: MessagesState = messagesAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  selectedMessageId: null,
  newMessageDraft: null,
  isMessageResponsesLoading: false,
  detailError: null,
});

export const messagesFeatureKey = 'messages';

export const messagesReducer = createReducer(
  initialState,
  on(actions.getMessages, (state): MessagesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
    };
  }),

  on(actions.getMessagesSuccess, (state, { messages }): MessagesState => {
    return messagesAdapter.addMany(messages, state);
  }),

  on(actions.getMessagesSuccess, (state, { pageNumber, isAtEndOfData, totalRecordCount }): MessagesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
        pageNumber,
        isAtEndOfData,
        totalRecordCount,
        pageSize: baseState.DEFAULT_PAGE_SIZE,
      },
    };
  }),

  // Split apart in PBI 54129. These two shared one handler, so a failed message-thread read wrote the
  // slot the messages LIST error state reads: after being bounced to /error and pressing "Try again"
  // (which is location.back()), the patient landed on a healthy list showing "Could not load your
  // messages". The thread failure now lands in detailError instead.
  on(actions.getMessagesFailure, (state, { error }): MessagesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  on(actions.getMessageResponsesFailure, (state, { error }): MessagesState => {
    return {
      ...state,
      detailError: error,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
    };
  }),

  on(actions.getMessages, (state): MessagesState => messagesAdapter.removeAll(state)),

  // Retry of a failed page: clear the error and show loading, keeping pageNumber and every loaded
  // message. Deliberately no removeAll — that is the difference from getMessages above.
  on(
    actions.retryPage,
    (state): MessagesState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(actions.setPageNumber, (state, { pageNumber }): MessagesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber,
      },
    };
  }),

  on(actions.selectMessage, (state, { messageId }): MessagesState => {
    return {
      ...state,
      selectedMessageId: messageId,
      newMessageDraft: null,
    };
  }),

  on(actions.buildNewMessageDraft, (state, { providerUserId, messageTopicId }): MessagesState => {
    return {
      ...state,
      newMessageDraft: { providerUserId, messageTopicId },
      selectedMessageId: null,
    };
  }),

  on(actions.discardNewMessageDraft, (state): MessagesState => {
    return {
      ...state,
      newMessageDraft: null,
    };
  }),

  on(actions.clearSelectedMessage, (state): MessagesState => {
    return {
      ...state,
      selectedMessageId: null,
    };
  }),

  on(actions.leaveMessagesPage, (state): MessagesState => {
    return {
      ...state,
      selectedMessageId: null,
      newMessageDraft: null,
    };
  }),

  on(actions.getMessageResponses, (state): MessagesState => {
    return {
      ...state,
      isMessageResponsesLoading: true,
      detailError: null,
    };
  }),

  on(actions.getMessageResponsesSuccess, actions.getMessageResponsesFailure, (state): MessagesState => {
    return {
      ...state,
      isMessageResponsesLoading: false,
    };
  }),

  on(actions.getMessageResponsesSuccess, (state, { message }): MessagesState => {
    const stateWithoutStaleDetailError = {
      ...state,
      detailError: null,
    };
    const i = state.ids.findIndex((mi: string | number) => mi == message.messageId);
    if (i < 0) return messagesAdapter.addOne(message, stateWithoutStaleDetailError);

    const updatedMessage: Update<Message> = {
      id: message.messageId,
      changes: {
        messageResponses: message.messageResponses,
      },
    };

    return messagesAdapter.updateOne(updatedMessage, stateWithoutStaleDetailError);
  }),

  on(actions.sendMessageResponseSuccess, (state, { messageResponse }): MessagesState => {
    const message = state.entities[messageResponse.messageId];

    if (!message) return state;

    const responses = structuredClone(message.messageResponses || []);
    responses.push(messageResponse);

    const updatedMessage: Update<Message> = {
      id: message.messageId,
      changes: {
        mostRecentDisplayName: messageResponse.displayName,
        mostRecentMessageResponse: messageResponse.body,
        modifiedOnUtc: messageResponse.createdOnUtc,
        messageResponses: responses,
      },
    };

    return messagesAdapter.updateOne(updatedMessage, state);
  }),

  on(actions.sendMessageSuccess, (state, { message }): MessagesState => messagesAdapter.addOne(message, state)),

  on(actions.sendMessageSuccess, (state, { message }): MessagesState => {
    return {
      ...state,
      selectedMessageId: message.messageId,
    };
  }),

  on(setGlobalPageSize, (state, { pageSize }): MessagesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        pageSize,
      },
    };
  })
);
